import { useEffect, useMemo, useState } from 'react';

// The countdown is capped to keep parity with the old Chat answer timer, where
// the displayed remaining time never exceeded 15 minutes regardless of the
// backend deadline.
const MAX_COUNTER_DURATION_SECONDS = 900;
const TICK_INTERVAL_MS = 1000;
const SECONDS_IN_MINUTE = 60;

interface UseLeadAnswerTimerParams {
  isSpam: boolean;
  isNoAnswer: boolean;
  userAnswerTill: string | null;
  serverTimeUtc: string | null;
}

interface UseLeadAnswerTimerResult {
  shouldShowAnswerTimer: boolean;
  countdownLabel: string;
}

// Matches a trailing timezone designator (`Z` or `±hh[:]mm`).
const TIMEZONE_DESIGNATOR_REGEXP = /(?:Z|[+-]\d{2}:?\d{2})$/i;

// Backend `date-time` values (ServerTimeUtc / UserAnswerTill) are UTC but are
// often serialized without a `Z` suffix. `new Date('2026-06-24T10:00:00')` would
// read such a string as local time, shifting the answer deadline into the past
// and freezing the countdown at "0 мин.". Force UTC parsing to mirror the old
// `moment.utc(...)` behaviour so both timestamps share the same basis.
const parseUtcTimeMs = (value: string | null): number | null => {
  if (!value) return null;

  const trimmed = value.trim();
  if (!trimmed) return null;

  const utcValue = TIMEZONE_DESIGNATOR_REGEXP.test(trimmed)
    ? trimmed
    : `${trimmed}Z`;
  const timeMs = new Date(utcValue).getTime();

  return Number.isFinite(timeMs) ? timeMs : null;
};

const formatCountdown = (totalSeconds: number): string => {
  if (totalSeconds <= 0) return '0 мин.';

  const minutes = Math.floor(totalSeconds / SECONDS_IN_MINUTE);
  const seconds = totalSeconds % SECONDS_IN_MINUTE;

  return `${minutes} мин. ${seconds} сек.`;
};

export const useLeadAnswerTimer = ({
  isSpam,
  isNoAnswer,
  userAnswerTill,
  serverTimeUtc,
}: UseLeadAnswerTimerParams): UseLeadAnswerTimerResult => {
  const deadlineMs = useMemo(
    () => parseUtcTimeMs(userAnswerTill),
    [userAnswerTill]
  );
  const serverNowMs = useMemo(
    () => parseUtcTimeMs(serverTimeUtc),
    [serverTimeUtc]
  );

  const shouldShowAnswerTimer =
    !isSpam && isNoAnswer && deadlineMs !== null && serverNowMs !== null;

  const [remainingSeconds, setRemainingSeconds] = useState(0);

  useEffect(() => {
    if (!shouldShowAnswerTimer || deadlineMs === null || serverNowMs === null) {
      setRemainingSeconds(0);
      return;
    }

    // Anchor the countdown to the client clock at mount so a delayed interval
    // tick never drifts: remaining = serverDeadlineGap - clientElapsed.
    const clientAnchorMs = Date.now();
    const initialRemainingSeconds = Math.min(
      MAX_COUNTER_DURATION_SECONDS,
      Math.max(0, Math.round((deadlineMs - serverNowMs) / 1000))
    );

    const computeRemaining = (): number => {
      const elapsedSeconds = Math.floor((Date.now() - clientAnchorMs) / 1000);

      return Math.max(0, initialRemainingSeconds - elapsedSeconds);
    };

    setRemainingSeconds(computeRemaining());

    const intervalId = window.setInterval(() => {
      const nextRemaining = computeRemaining();

      setRemainingSeconds(nextRemaining);

      if (nextRemaining <= 0) {
        window.clearInterval(intervalId);
      }
    }, TICK_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [shouldShowAnswerTimer, deadlineMs, serverNowMs]);

  const countdownLabel = useMemo(
    () => formatCountdown(remainingSeconds),
    [remainingSeconds]
  );

  return { shouldShowAnswerTimer, countdownLabel };
};
